import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faSave, faSpinner } from '@fortawesome/free-solid-svg-icons';
import './NotificationPreferences.css';
import { API_ENDPOINTS } from '../../config/api';
import { fetchAuthSession } from 'aws-amplify/auth';

const NotificationPreferences = ({ onClose }) => {
  const [preferences, setPreferences] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const collections = [
    { name: 'ClientInfo', label: 'Client Information' },
    { name: 'ClientNote', label: 'Client Notes' },
    { name: 'Contracts', label: 'Contracts' },
    { name: 'Leads', label: 'Leads' },
    { name: 'survey_responses', label: 'Survey Responses' },
    { name: 'snap_monetization_forms', label: 'Monetization Forms' }
  ];

  useEffect(() => {
    fetchPreferences();
  }, []);

  const fetchPreferences = async () => {
    try {
      setLoading(true);
      const { tokens } = await fetchAuthSession();
      const token = tokens.idToken.toString();

      const response = await axios.get(
        API_ENDPOINTS.NOTIFICATIONS.GET_PREFERENCES,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.status === 'success') {
        setPreferences(response.data.preferences);
      }
    } catch (error) {
      console.error('Error fetching preferences:', error);
      setMessage({ type: 'error', text: 'Failed to load preferences' });
    } finally {
      setLoading(false);
    }
  };

  const savePreferences = async () => {
    try {
      setSaving(true);
      const { tokens } = await fetchAuthSession();
      const token = tokens.idToken.toString();

      const response = await axios.put(
        API_ENDPOINTS.NOTIFICATIONS.UPDATE_PREFERENCES,
        { preferences },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.status === 'success') {
        setMessage({ type: 'success', text: 'Preferences saved successfully!' });
        setTimeout(() => setMessage(null), 3000);
      }
    } catch (error) {
      console.error('Error saving preferences:', error);
      setMessage({ type: 'error', text: 'Failed to save preferences' });
    } finally {
      setSaving(false);
    }
  };

  const updatePreference = (path, value) => {
    setPreferences(prev => {
      const updated = { ...prev };
      const keys = path.split('.');
      let current = updated;
      
      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) {
          current[keys[i]] = {};
        }
        current = current[keys[i]];
      }
      
      current[keys[keys.length - 1]] = value;
      return updated;
    });
  };

  const toggleCollection = (collectionName) => {
    const currentValue = preferences?.collections?.[collectionName]?.enabled ?? true;
    updatePreference(`collections.${collectionName}.enabled`, !currentValue);
  };

  if (loading) {
    return (
      <div className="notif-prefs-page">
        <div className="notif-prefs-loading">
          <FontAwesomeIcon icon={faSpinner} spin size="2x" />
          <p>Loading preferences...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="notif-prefs-page">
      <div className="notif-prefs-header">
        <button className="notif-prefs-back-btn" onClick={onClose}>
          <FontAwesomeIcon icon={faArrowLeft} />
          <span>Back to Notifications</span>
        </button>
        <h1>Dashboard Notifications</h1>
      </div>

      {message && (
        <div className={`notif-prefs-message ${message.type}`}>
          {message.text}
        </div>
      )}

      <div className="notif-prefs-container">
        <div className="notif-prefs-section">
          <h2>Client Filter</h2>
          <div className="notif-prefs-item">
            <div className="notif-prefs-info">
              <label>Assigned Clients Only</label>
              <p>Only receive notifications for clients assigned to you</p>
            </div>
            <label className="notif-prefs-toggle">
              <input
                type="checkbox"
                checked={preferences?.assigned_clients_only ?? true}
                onChange={(e) => updatePreference('assigned_clients_only', e.target.checked)}
              />
              <span className="notif-prefs-toggle-slider"></span>
            </label>
          </div>
        </div>

        <div className="notif-prefs-section">
          <h2>Collection Notifications</h2>
          <p className="notif-prefs-section-desc">
            Choose which types of client changes you want to be notified about
          </p>
          <div className="notif-prefs-collections-grid">
            {collections.map((collection) => (
              <div key={collection.name} className="notif-prefs-collection-card">
                <div className="notif-prefs-collection-header">
                  <h3>{collection.label}</h3>
                  <label className="notif-prefs-toggle">
                    <input
                      type="checkbox"
                      checked={preferences?.collections?.[collection.name]?.enabled ?? true}
                      onChange={() => toggleCollection(collection.name)}
                    />
                    <span className="notif-prefs-toggle-slider"></span>
                  </label>
                </div>
                <div className="notif-prefs-collection-status">
                  {preferences?.collections?.[collection.name]?.enabled ?? true
                    ? 'Notifications enabled'
                    : 'Notifications disabled'
                  }
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="notif-prefs-section">
          <h2>Priority Notifications</h2>
          <div className="notif-prefs-priority-info">
            <div className="notif-prefs-priority-item">
              <div className="notif-prefs-priority-badge high">HIGH</div>
              <div className="notif-prefs-priority-desc">
                <strong>Monetized Clients</strong>
                <p>Critical updates from monetized creators</p>
              </div>
            </div>
            <div className="notif-prefs-priority-item">
              <div className="notif-prefs-priority-badge medium">MEDIUM</div>
              <div className="notif-prefs-priority-desc">
                <strong>Signed Clients</strong>
                <p>Updates from clients under contract</p>
              </div>
            </div>
            <div className="notif-prefs-priority-item">
              <div className="notif-prefs-priority-badge low">LOW</div>
              <div className="notif-prefs-priority-desc">
                <strong>Other Activity</strong>
                <p>General client activity and updates</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="notif-prefs-footer">
        <button
          className="notif-prefs-save-btn"
          onClick={savePreferences}
          disabled={saving}
        >
          {saving ? (
            <>
              <FontAwesomeIcon icon={faSpinner} spin />
              <span>Saving...</span>
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faSave} />
              <span>Save Preferences</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default NotificationPreferences;

